import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import ApiOutAccessB2b from 'api/b2b/ApiOutAccessManag'
import B2bTDPAPI from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import ApiOutAccessTable from './ApiOutAccessList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

export interface TableRowData {
  id: string
  name: string
  companyId: string
  status: boolean
  createdBy: string
  createdOn: string
  updatedBy: string
  updatedOn: string
}
interface SelectedCompany {
  name: string
  companyId: string
}

export interface TenantAccessInfo {
  id: string
  companyName: string
  tenant_id: string
  accessToken: string
  is_active: boolean
  created_by: string
  created_on: string
  updated_by: string
  updated_on: string
}

interface FormData {
  companyName: CompanyOption | null
}

function SearchAPIKey() {
  const navigate = useNavigate()
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [selectedCompanyData, setSelectedCompanyData] =
    useState<SelectedCompany | null>(null)
  const [tenantAccessData, setTenantAccessData] = useState<TableRowData[]>([])

  const debouncedSearchTerm = useDebounce(companySearchTerm, 500) // 500ms debounce

  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues: {
      companyName: null
    }
  })

  // Mutation for fetching tenant access info
  const {
    mutate: fetchTenantAccessInfo
  } = useMutation({
    mutationFn: async (companyId?: string) => {
      const token = OrbitSession.get('faberToken')
      const { data } = await ApiOutAccessB2b.getTenantAccessInfo(
        token,
        companyId
      )
      return data
    },
    onSuccess: (data: TenantAccessInfo[]) => {
      // Transforming the API response to match your table interface
      const formattedTableData: TableRowData[] = data.map(
        (item: TenantAccessInfo) => ({
          name: item.companyName || selectedCompany?.value || '',
          companyId: item.tenant_id,
          status: item.is_active,
          createdBy: item.created_by || '',
          createdOn: item.created_on || '',
          updatedBy: item.updated_by || '',
          updatedOn: item.updated_on || '',
          id: item.id
        })
      )

      setTenantAccessData(formattedTableData)
    },
    onError: (error) => {
      console.error('Failed to fetch tenant access info:', error)
      setTenantAccessData([])
    }
  })

  const selectedCompany = watch('companyName')

  // Debounced company search query
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
    // error: tenantError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2, // Only search when user types at least 2 characters
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  // const filterOptions = useCallback((options: CompanyOption[]) => {
  //   return options
  // }, [])

  const handleCompanySelect = useCallback(
    (selectedCompany: CompanyOption | null) => {
      if (selectedCompany) {
        const formattedData: SelectedCompany = {
          name: selectedCompany.label,
          companyId: selectedCompany.companyId
        }

        setSelectedCompanyData(formattedData)
      } else {
        setSelectedCompanyData(null)
      }
    },
    []
  )

  const onSubmit = () => {
    if (selectedCompanyData) {
      fetchTenantAccessInfo(selectedCompanyData.companyId)
    }
  }

  const onReset = () => {
    reset()
    setCompanySearchTerm('')
    setSelectedCompanyData(null)
    setTenantAccessData([])
  }

  return (
    <Box>
      <Box display='flex' justifyContent='flex-end'>
        <Button onClick={() => navigate('add')}>Add</Button>
      </Box>

      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: '10px', fontSize: '14px' }}>
            Company Name
          </Typography>
          <Controller
            name='companyName'
            control={control}
            rules={{ required: { value: true, message: 'Required' } }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                options={companyOptions}
                // filterOptions={filterOptions}
                loading={isTenantLoading}
                disablePortal
                size='small'
                sx={{ width: '250px' }}
                getOptionLabel={(option: CompanyOption | null) =>
                  option?.label || ''
                }
                isOptionEqualToValue={(
                  option: CompanyOption,
                  value: CompanyOption | null
                ) => option?.value === value?.value}
                onChange={(_, data: CompanyOption | null) => {
                  field.onChange(data)
                  handleCompanySelect(data)
                }}
                onInputChange={handleCompanyInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={
                      error?.message ||
                      (debouncedSearchTerm.length > 0 &&
                      debouncedSearchTerm.length < 2
                        ? 'Type at least 2 characters'
                        : '')
                    }
                    placeholder='Search company...'
                    InputProps={{
                      ...params.InputProps
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Box>
                      <Typography variant='body2'>{option.label}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        ID: {option.companyId}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  debouncedSearchTerm.length < 2
                    ? 'Type at least 2 characters to search'
                    : isTenantLoading
                    ? 'Searching...'
                    : tenantFetchError
                    ? 'Error loading companies'
                    : 'No companies found'
                }
              />
            )}
          />
        </Box>

        {/* Error display */}
        {tenantFetchError && (
          <Box sx={{ mb: 2 }}>
            <Typography variant='body2' color='error'>
              Error loading companies:
            </Typography>
          </Box>
        )}

        <Box mt='10px' sx={{ display: 'flex', gap: 1 }}>
          <Button variant='contained' type='submit' disabled={!selectedCompany}>
            Search
          </Button>
          <Button variant='outlined' type='button' onClick={onReset}>
            Reset
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <ApiOutAccessTable
          tenantAccessData={tenantAccessData}
          setTenantAccessData={setTenantAccessData}
        />
      </Box>
    </Box>
  )
}

export default SearchAPIKey
